import styled from "@emotion/styled";
import { Fragment } from "react";

import { MDSIcon, MDSIconKind, MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import type { AgentActivityReference } from "@/pages/agent/activities/agent-activity.types";

export interface AgentActivityNavigation {
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
  onOpenFollowUp: ({ followUpId }: { followUpId: string }) => void;
  onOpenRoutine: ({ routineId }: { routineId: string }) => void;
  onOpenNote: ({ noteId }: { noteId: string }) => void;
  onOpenCollection: ({ collectionId }: { collectionId: string }) => void;
}

interface TextSegment {
  kind: "text";
  text: string;
}

interface ReferenceSegment {
  kind: "reference";
  icon: MDSIconKind;
  label: string;
  reference: AgentActivityReference;
}

type ActivityTextSegment = TextSegment | ReferenceSegment;

export interface ResolvedAgentActivityText {
  hasLinkableReference: boolean;
  segments: ActivityTextSegment[];
}

const LINKABLE_REFERENCE_ICONS: Partial<Record<string, MDSIconKind>> = {
  note: MDSIconKind.Document,
  collection: MDSIconKind.Collection,
  project: MDSIconKind.Folder,
  task: MDSIconKind.ListCheck,
  follow_up: MDSIconKind.Clock,
  routine: MDSIconKind.Calendar,
};

const REFERENCE_TOKEN_PATTERN = /\[\[ref:(\d+)\]\]/g;
const CHIP_SUFFIX_MATCH_MIN_LENGTH = 8;

const resolveLinkableReferenceIcon = (kind: string): MDSIconKind | undefined =>
  Object.prototype.hasOwnProperty.call(LINKABLE_REFERENCE_ICONS, kind)
    ? LINKABLE_REFERENCE_ICONS[kind]
    : undefined;

export const resolveAgentActivityText = ({
  text,
  references,
}: {
  text: string;
  references: AgentActivityReference[];
}): ResolvedAgentActivityText => {
  const tokenMatches = [...text.matchAll(REFERENCE_TOKEN_PATTERN)];
  if (tokenMatches.length > 0) {
    const segments: ActivityTextSegment[] = [];
    let cursor = 0;
    for (const match of tokenMatches) {
      const token = match[0];
      const start = match.index;
      pushTextSegment(segments, text.slice(cursor, start));
      const reference = references[Number(match[1])];
      if (reference?.label) {
        const icon = resolveLinkableReferenceIcon(reference.kind);
        if (icon !== undefined) {
          segments.push({ kind: "reference", icon, label: reference.label, reference });
        } else {
          pushTextSegment(segments, reference.label);
        }
      } else {
        pushTextSegment(segments, token);
      }
      cursor = start + token.length;
    }
    pushTextSegment(segments, text.slice(cursor));
    return {
      hasLinkableReference: segments.some(segment => segment.kind === "reference"),
      segments,
    };
  }

  return resolveLegacyActivityText({ text, references });
};

const resolveLegacyActivityText = ({
  text,
  references,
}: {
  text: string;
  references: AgentActivityReference[];
}): ResolvedAgentActivityText => {
  const chips = references
    .map(reference => {
      const icon = resolveLinkableReferenceIcon(reference.kind);
      return {
        icon,
        reference,
        matchedText: icon === undefined ? null : resolveLegacyChipText({ text, reference }),
      };
    })
    .filter(
      (
        chip
      ): chip is {
        icon: MDSIconKind;
        reference: AgentActivityReference;
        matchedText: string;
      } => chip.icon !== undefined && Boolean(chip.matchedText)
    )
    .sort((left, right) => text.indexOf(left.matchedText) - text.indexOf(right.matchedText));
  const segments: ActivityTextSegment[] = [];
  let cursor = 0;
  for (const { icon, reference, matchedText } of chips) {
    let start = text.indexOf(matchedText, cursor);
    if (start < cursor) continue;
    let end = start + matchedText.length;
    if (text[start - 1] === '"' && text[end] === '"') {
      start -= 1;
      end += 1;
    }
    pushTextSegment(segments, text.slice(cursor, start));
    segments.push({ kind: "reference", icon, label: matchedText, reference });
    cursor = end;
  }
  pushTextSegment(segments, text.slice(cursor));
  return {
    hasLinkableReference: segments.some(segment => segment.kind === "reference"),
    segments,
  };
};

const resolveLegacyChipText = ({
  text,
  reference,
}: {
  text: string;
  reference: AgentActivityReference;
}): string | null => {
  const label = reference.label;
  if (!label) return null;
  if (text.includes(label)) return label;
  const dashIndex = label.lastIndexOf("—");
  if (dashIndex === -1) return null;
  const suffix = label.slice(dashIndex + 1).trim();
  return suffix.length >= CHIP_SUFFIX_MATCH_MIN_LENGTH && text.includes(suffix) ? suffix : null;
};

const pushTextSegment = (segments: ActivityTextSegment[], text: string) => {
  if (!text) return;
  const previous = segments.at(-1);
  if (previous?.kind === "text") {
    previous.text += text;
  } else {
    segments.push({ kind: "text", text });
  }
};

export const AgentActivityText = ({
  resolved,
  weight,
  ...navigation
}: {
  resolved: ResolvedAgentActivityText;
  weight?: MDSTextWeight;
} & AgentActivityNavigation) => (
  <MDSText color={MDSTextColor.Primary} variant={MDSTextVariant.TextS} weight={weight}>
    {resolved.segments.map((segment, index) => (
      <Fragment key={index}>
        {segment.kind === "text" ? (
          segment.text
        ) : (
          <ReferenceButton segment={segment} navigation={navigation} />
        )}
      </Fragment>
    ))}
  </MDSText>
);

const ReferenceButton = ({
  segment,
  navigation,
}: {
  segment: ReferenceSegment;
  navigation: AgentActivityNavigation;
}) => (
  <StyledReferenceButton
    type="button"
    onClick={event => {
      event.stopPropagation();
      const { reference } = segment;
      if (reference.kind === "project") {
        navigation.onOpenProject({ projectId: reference.id });
      } else if (reference.kind === "task") {
        navigation.onOpenTask({ taskId: reference.id });
      } else if (reference.kind === "follow_up") {
        navigation.onOpenFollowUp({ followUpId: reference.id });
      } else if (reference.kind === "routine") {
        navigation.onOpenRoutine({ routineId: reference.id });
      } else if (reference.kind === "note") {
        navigation.onOpenNote({ noteId: reference.id });
      } else if (reference.kind === "collection") {
        navigation.onOpenCollection({ collectionId: reference.id });
      }
    }}
  >
    <MDSIcon kind={segment.icon} size={14} />
    <StyledReferenceLabel>{segment.label}</StyledReferenceLabel>
  </StyledReferenceButton>
);

const StyledReferenceButton = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  gap: ${({ theme }) => theme.spacing[1]};
  padding: 0;
  text-align: left;
  vertical-align: bottom;

  &:hover span,
  &:focus-visible span {
    text-decoration-style: solid;
  }
`;

const StyledReferenceLabel = styled.span`
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
`;
